<template>
  <div class="user-list">
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增用户按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'user:edit'">新增用户</el-button>
        <!-- 批量删除按钮（选中用户后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'user:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('user:edit') || selectedIds.length === 0">批量删除</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="账号">
            <el-input v-model="searchForm.username" placeholder="请输入账号" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="姓名">
            <el-input v-model="searchForm.name" placeholder="请输入姓名" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="角色">
            <el-select v-model="searchForm.roleId" placeholder="请选择角色" clearable style="width: 100%">
              <el-option v-for="role in allRoles" :key="role.id" :label="role.roleName" :value="role.id"></el-option>
            </el-select>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadUsers">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="users" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="username" label="账号" header-align="center"></el-table-column>
      <el-table-column prop="name" label="姓名" header-align="center"></el-table-column>
      <el-table-column prop="email" label="邮箱" header-align="center"></el-table-column>
      <el-table-column prop="phone" label="手机号" header-align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="组织机构" header-align="center">
        <template slot-scope="scope" v-if="scope.row.username !== 'admin'">
          <el-tag v-for="org in scope.row.orgs" :key="org.id" type="info" size="mini" class="mr-1">
            {{ org.orgName }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="角色" header-align="center">
        <template slot-scope="scope">
          <el-tag v-for="role in scope.row.roles" :key="role.id" type="info" size="mini" class="mr-1">
            {{ role.roleName }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="300">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'user:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="分配组织" placement="top">
            <el-button icon="el-icon-office-building" type="success" circle v-hasPermission="'user:edit'"
              @click="openAssignOrgDialog(scope.row)" :disabled="scope.row.username === 'admin'"></el-button>
          </el-tooltip>
          <el-tooltip content="分配角色" placement="top">
            <el-button icon="el-icon-user" type="warning" circle v-hasPermission="'user:edit'"
              @click="openAssignRolesDialog(scope.row)" :disabled="scope.row.username === 'admin'"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'user:edit'"
              @click="handleDelete(scope.row.id)" :disabled="scope.row.username === 'admin'"></el-button>
          </el-tooltip>
          <el-tooltip content="重置密码" placement="top">
            <el-button icon="el-icon-refresh-right" type="info" circle v-hasPermission="'user:resetPwd'"
              @click="handleResetPassword(scope.row)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="账号" prop="username">
          <el-input v-model="form.username" :disabled="isEdit" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="form.name" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" maxlength="30"></el-input>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="form.phone" maxlength="11"
            @input="form.phone = form.phone.replace(/[^\d]/g, '')"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配组织机构对话框 -->
    <el-dialog title="分配组织机构" :visible.sync="assignOrgVisible" width="700px">
      <el-transfer :data="organizations" filterable="true" filter-placeholder="请输入组织机构名称" :titles="['可选组织机构', '已选组织机构']"
        :props="{ key: 'id', label: 'orgName' }" v-model="selectedOrgIds"></el-transfer>
      <div slot="footer">
        <el-button @click="assignOrgVisible = false">取消</el-button>
        <el-button type="primary" @click="saveOrgs">确定</el-button>
      </div>
    </el-dialog>
    <!-- 分配角色对话框 -->
    <el-dialog title="分配角色" :visible.sync="assignRolesVisible" width="700px">
      <el-transfer :data="filteredRoles" :titles="['可选角色', '已选角色']" :props="{ key: 'id', label: 'roleName' }"
        v-model="selectedRoleIds"></el-transfer>
      <div slot="footer">
        <el-button @click="assignRolesVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRoles">确定</el-button>
      </div>
    </el-dialog>
    <!-- 重置密码确认弹窗 -->
    <el-dialog title="重置密码" :visible.sync="resetPwdDialogVisible" width="20%" :close-on-click-modal="false">
      <div>确定要将 <span style="color: #f56c6c">{{ resetTargetUser?.username }}</span> 的密码重置为默认密码（123456）吗？</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resetPwdDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmResetPassword">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import store from '../../store';
import userApi from '../../api/user'
import roleApi from '../../api/role'
import orgApi from '../../api/organization'
import { filterSuperAdminRole } from '../../utils/roleFilter'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'UserList',
  data() {
    // 账号唯一校验
    const checkUsernameUnique = async (rule, value, callback) => {
      if (!value) {
        // 空值不校验（由required规则处理）
        return callback()
      }

      try {
        const params = {
          username: value,
          excludeId: this.isEdit ? this.recordId : null
        }
        // 调用后端查重接口
        const response = await userApi.checkUsername(params)
        if (response.success && response.exists) {
          // 账号已存在，返回错误提示
          callback(new Error('该账号已被使用，请更换'))
        } else {
          // 账号可用，验证通过
          callback()
        }
      } catch (error) {
        callback(new Error('检查失败，请稍后重试'))
      }
    }

    return {
      // 表格数据
      users: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,

      organizations: [],

      // 搜索表单
      searchForm: {
        username: '',
        name: '',
        roleId: null
      },

      // 选中的行
      selectedIds: [],

      resetPwdDialogVisible: false, // 重置密码弹窗开关
      resetTargetUser: null, // 存储要重置密码的用户信息

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增用户',
      isEdit: false,
      recordId: null, // 编辑时记录当前账号的ID，用于排除自身
      form: {
        id: null,
        username: '',
        name: '',
        email: '',
        phone: '',
        status: 1
      },
      rules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]+$/, message: '账号只能包含字母和数字', trigger: 'blur' },
          { min: 4, max: 20, message: '账号长度在 4 到 20 个字符', trigger: 'blur' },
          { validator: checkUsernameUnique, trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' },
          { min: 1, max: 20, message: '姓名长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱格式', trigger: ['blur', 'change'] },
          { min: 1, max: 30, message: '邮箱长度在 1 到 30 个字符', trigger: 'blur' }
        ],
        phone: [
          { min: 11, max: 11, message: '手机号长度必须为11位', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ]
      },

      selectedUserId: null,
      // 分配组织机构
      assignOrgVisible: false,
      selectedOrgIds: [],
      // 分配角色
      assignRolesVisible: false,
      selectedRoleIds: [],

      currentUserId: store.state.user.userInfo.id,
      allRoles: [],
    }
  },

  created() {
    this.loadUsers()
    this.loadAllRoles()
    this.loadOrganizations()
  },

  computed: {
    // 计算属性：过滤后的角色列表
    filteredRoles() {
      // 调用工具函数：非超级管理员（ID≠1）则过滤掉角色ID=1的超级管理员
      return filterSuperAdminRole(this.allRoles, this.currentUserId);
    },
  },

  methods: {
    // 加载用户列表
    loadUsers() {
      // 组装请求参数
      const params = {
        username: this.searchForm.username, // 搜索-账号
        name: this.searchForm.name,       // 搜索-姓名
        roleId: this.searchForm.roleId,   // 搜索-角色
        page: this.currentPage,           // 分页-当前页
        size: this.pageSize               // 分页-每页条数
      };
      userApi.findAll(params).then(response => {
        if (response.success) {
          this.users = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载用户失败：' + error.message)
      })
    },

    // 加载所有组织机构
    loadOrganizations() {
      orgApi.findAll().then(response => {
        if (response.success) {
          this.organizations = response.data.data
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载组织机构失败：' + error.message)
      })
    },

    // 加载所有角色
    loadAllRoles() {
      roleApi.findAll().then(response => {
        if (response.success) {
          this.allRoles = response.data.data
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载角色失败：' + error.message)
      })
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        username: '',
        name: '',
        roleId: null
      }
      this.loadUsers()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadUsers(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadUsers(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('user:edit')) {
        this.$message.error('无新增权限');
        return;
      }
      this.dialogTitle = '新增用户'
      this.isEdit = false
      this.recordId = null
      this.form = {
        id: null,
        username: '',
        name: '',
        email: '',
        phone: '',
        status: 1
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('user:edit')) {
        this.$message.error('无编辑权限');
        return;
      }
      this.dialogTitle = '编辑用户'
      this.isEdit = true
      this.recordId = row.id
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新用户
            userApi.update(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadUsers()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增用户
            userApi.add(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadUsers()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除用户
    handleDelete(id) {
      if (!hasPermission('user:edit')) {
        this.$message.error('无删除权限');
        return;
      }
      this.$confirm('确定要删除该用户吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        userApi.delete(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadUsers()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除用户
    handleBatchDelete() {
      if (!hasPermission('user:edit')) {
        this.$message.error('无删除权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的用户');
        return;
      }
      this.$confirm('确定要批量删除选中的用户吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        userApi.deleteBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadUsers()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 打开分配组织机构对话框
    openAssignOrgDialog(row) {
      if (!hasPermission('user:edit')) {
        this.$message.error('无分配组织机构权限');
        return;
      }
      this.selectedUserId = row.id
      this.assignOrgVisible = true
      // 获取已选组织机构ID
      this.selectedOrgIds = row.orgs.map(org => org.id)
    },

    // 打开分配角色对话框
    openAssignRolesDialog(row) {
      if (!hasPermission('user:edit')) {
        this.$message.error('无分配角色权限');
        return;
      }
      this.selectedUserId = row.id
      this.assignRolesVisible = true
      // 获取已选角色ID
      this.selectedRoleIds = row.roles.map(role => role.id)
    },

    // 保存组织机构分配
    saveOrgs() {
      userApi.assignOrgs(this.selectedUserId, this.selectedOrgIds).then(response => {
        if (response.success) {
          this.$message.success(response.message || '组织机构分配成功')
          this.assignOrgVisible = false
          this.loadUsers()
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('组织机构分配失败：' + error.message)
      })
    },

    // 保存角色分配
    saveRoles() {
      userApi.assignRoles(this.selectedUserId, this.selectedRoleIds).then(response => {
        if (response.success) {
          this.$message.success(response.message || '角色分配成功')
          this.assignRolesVisible = false
          this.loadUsers()
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('角色分配失败：' + error.message)
      })
    },

    // 点击“重置密码”按钮：打开弹窗并记录目标用户
    handleResetPassword(row) {
      if (!hasPermission('user:resetPwd')) {
        this.$message.error('无重置密码权限');
        return;
      }
      this.resetTargetUser = row; // 存储当前用户信息（用于弹窗显示和后续请求）
      this.resetPwdDialogVisible = true; // 打开弹窗
    },

    // 确认重置密码：调用后端接口
    confirmResetPassword() {
      // 调用后端重置密码接口（传递用户ID）
      userApi.resetPassword(this.resetTargetUser.id).then(res => {
        this.$message.success(res.message || '密码重置成功，默认密码为123456');
        this.resetPwdDialogVisible = false; // 关闭弹窗
      }).catch(err => {
        this.$message.error('重置失败：' + (err.response?.data?.msg || '网络错误'));
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },
  }
}
</script>

<style scoped>
/* 穿透作用域，强制 el-transfer 整体左对齐 */
::v-deep .el-transfer {
  text-align: left !important;
  /* 覆盖全局的 center */
}

/* 确保左右面板内的内容也左对齐 */
::v-deep .el-transfer-panel {
  text-align: left !important;
}

/* 修复选项项（复选框+文字）的对齐 */
::v-deep .el-transfer-panel__item {
  display: flex;
  align-items: center;
  /* 复选框与文字垂直居中 */
}
</style>